import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CoverPicker from '../components/CoverPicker/CoverPicker';
import MissingCoverPicker from '../components/CoverPicker/MissingCoverPicker';
import { RootState, AppDispatch } from '../store';
import {
	Platform,
	DeviceType,
	Orientation,
	CoverItem,
	ImageItem,
	RangeSettings,
	SofaRangeName,
	Lightbox,
	WriteDirection,
	Settings,
	SofaConfigurationItem,
	MissingProductItem,
	PredefinedConfiguration,
	ShoppingItem,
	SofaConfiguration,
	ModelsQuantity,
} from 'sofa-reborn-react';
import {
	setSelectedCover,
	showCoverProductDescription,
	setAdditionalCoverItems,
	clearAdditionalCoverItems,
	additionalCoverItemClicked,
	addAdditionalCover,
	additionalCoverClosed,
} from '../store/cover/actions';
import { setCoverItem } from '../store/application/thunks';
import { ProductUtils } from '../util/ProductUtils';
import { Product, ProductData } from '@ikea-aoa/component-view-3d';
import { setCurrentLightbox, setThumbnailImageChanged } from '../store/application/actions';
import { IDexfSettings, IShoppingProduct, ITranslations } from '@inter-ikea-kompis/types';
import { setMissingCover } from '../store/cover/actions';
import { setSelectedConfiguration, setMissingConfiguration } from '../store/sofaConfiguration';
import { ProductService, KompisDefaultFields } from '@inter-ikea-kompis/services';
import {
	setMissingCoverSectionHasBeenShown,
	buildTipHasBeenShown,
	hideBuildTip,
} from '../store/popOver';

const getNrOfEmptyElements = (elementsPerRow: number, covers: CoverItem[]): number => {
	if (covers.length > elementsPerRow && covers.length % elementsPerRow !== 0) {
		return elementsPerRow - (covers.length % elementsPerRow);
	}

	return 0;
};

type Props = { isAdditionalCover?: boolean };

const CoverPickerContainer: React.FC<Props> = (props) => {
	const additionalCoverItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.cover.additionalCoverItems
	);
	const covers = useSelector<RootState, CoverItem[]>((state) => state.cover.coverItems);
	const coverOnlyItems = useSelector<RootState, CoverItem[]>((state) => state.cover.coverOnlyItems);
	const replaceCovers = useSelector<RootState, CoverItem[]>(
		(state) => state.cover.replaceCoverItems
	);
	const missingCover = useSelector<RootState, boolean>((state) => state.cover.missingCover);
	const selectedCoverState = useSelector<RootState, CoverItem>(
		(state) => state.cover.selectedCover as CoverItem
	);
	const selectedCover =
		missingCover && !replaceCovers.includes(selectedCoverState)
			? replaceCovers[0]
			: selectedCoverState;
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const products = useSelector<RootState, ProductData>((state) => state.products.products);
	const missingProducts = useSelector<RootState, MissingProductItem[]>(
		(state) => state.products.missingProducts
	);
	const missingConfiguration = useSelector<RootState, PredefinedConfiguration>(
		(state) => state.sofaConfiguration.missingConfiguration as PredefinedConfiguration
	);
	const sofaConfiguration = useSelector<RootState, SofaConfiguration>(
		(state) => state.sofaConfiguration.sofaConfiguration
	);
	const shoppingItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.sofaConfiguration.shoppingItems
	);
	const stockedIkeaPlus = useSelector<RootState, boolean>(
		(state) => state.checkout.stockedIkeaPlus
	);

	const [availableCovers, setAvailableCovers] = useState<CoverItem[]>([]);
	const [productDescriptionOpen, setProductDescriptionOpen] = useState(false);
	const [additionalCoverItemsProducts, setCoverItemsShoppingProduct] = useState<IShoppingProduct[]>(
		[]
	);
	const [visible, setVisible] = useState(false);
	const [activeCover, setActiveCover] = useState<CoverItem>(selectedCover);
	const [selectedIndex, setSelectedIndex] = useState(0);

	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		if (currentLightbox === 'cover-picker' || currentLightbox === 'additional-cover-picker') {
			if (!missingCover) {
				setCovers();
			}
			setTimeout(() => {
				setVisible(true);
			}, 100);
		}
	}, [currentLightbox]);

	useEffect(() => {
		getCoverItemsShoppingProducts();
	}, [activeCover]);

	const onCoverClick = (cover: CoverItem): void => {
		if (cover !== activeCover) {
			setActiveCover(cover);
			setSelectedIndex(0);

			if (props.isAdditionalCover) {
				dispatch(additionalCoverItemClicked(cover.coverName));
			} else {
				dispatch(setSelectedCover(cover, 'cover-picker'));
				dispatch(setCoverItem(cover, true));
				dispatch(clearAdditionalCoverItems());
				dispatch(setMissingCoverSectionHasBeenShown(false));
			}
			dispatch(hideBuildTip(true));
			setTimeout(() => {
				dispatch(buildTipHasBeenShown(true));
				dispatch(hideBuildTip(false));
			}, 3000);
		}
	};

	const onClose = (): void => {
		if (props.isAdditionalCover) {
			dispatch(additionalCoverClosed());
		}
		setVisible(false);
		setTimeout(() => {
			dispatch(setCurrentLightbox('none'));
		}, 100);
	};

	const onSkipListing = (): void => {
		const descriptionContainer = document.getElementById('description-container');
		if (descriptionContainer) {
			descriptionContainer.tabIndex = 0;
			descriptionContainer.focus();
		}
	};

	async function onAddAdditionalCoverClick(cover: CoverItem): Promise<void> {
		const newAdditionalCoverItems: ShoppingItem[] = await getAdditionalCoverItems(cover);
		if (additionalCoverItems) {
			newAdditionalCoverItems.push(...additionalCoverItems);
		}

		dispatch(addAdditionalCover(cover.coverName));
		dispatch(setAdditionalCoverItems(newAdditionalCoverItems));
		dispatch(setCurrentLightbox('none'));
	}

	async function getAdditionalCoverItems(cover: CoverItem): Promise<ShoppingItem[]> {
		const newAdditionalCoverItems: ShoppingItem[] = [];
		if (sofaConfiguration.configuration) {
			const modelIds: string[] = ProductUtils.getModelIdsForConfiguration(
				products,
				missingProducts,
				sofaConfiguration.configuration.children as SofaConfigurationItem[],
				rangeSettings.shouldColorAllModules as boolean,
				true
			);
			const modelsWithQuantity: ModelsQuantity = ProductUtils.getModelQuantity(modelIds);

			Object.values(products).forEach((p) => {
				if (
					modelIds.includes(p.modelId) &&
					p.filter.color === cover.coverName &&
					!p.productId.includes('backrest')
				) {
					const quantity = modelsWithQuantity[p.modelId];

					if (quantity) {
						p.sprItems?.forEach(async (childItem) => {
							const id: string = childItem.localId || childItem.itemno;
							const item: ShoppingItem = {
								id: id,
								quantity: quantity,
								type: 'ART',
							};
							let exists = false;

							const ikeaProduct: IShoppingProduct[] = await ProductUtils.getAdditionalCoverProducts(
								settings,
								[item]
							);

							if (ikeaProduct.length) {
								newAdditionalCoverItems.forEach((c) => {
									if (c.id === id) {
										c.quantity = c.quantity + quantity;
										exists = true;
									}
								});

								if (!exists) {
									newAdditionalCoverItems.push(item);
									modelsWithQuantity[p.modelId] = null;
								}
							}
						});
					}
				}
			});
		}

		return newAdditionalCoverItems;
	}

	async function setAvailableAdditionalCovers(): Promise<void> {
		const filterAsync = (array: CoverItem[], filter) =>
			Promise.all(array.map((entry) => filter(entry))).then((bits) =>
				array.filter(() => bits.shift())
			);

		filterAsync(covers, async function (cover: CoverItem) {
			const productService = new ProductService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
			});
			const modelIds: string[] = ProductUtils.getModelIdsForConfiguration(
				products,
				missingProducts,
				sofaConfiguration.configuration.children as SofaConfigurationItem[],
				rangeSettings.shouldColorAllModules as boolean,
				true
			);
			const itemsInConfiguration: ShoppingItem[] = await productService
				.getShoppingProducts(shoppingItems, KompisDefaultFields)
				.then((products) => ProductUtils.getCoverOnlyArticles(products));
			const coverItemsInConfiguration: IShoppingProduct[] =
				await ProductUtils.getAdditionalCoverProducts(settings, itemsInConfiguration);

			const productsInConfiguration: Product[] = Object.values(products).filter(
				(p) =>
					modelIds.includes(p.modelId) &&
					p.filter.color === cover.coverName &&
					!p.productId.includes('backrest')
			);
			const articlesInConfiguration: ShoppingItem[] = [];

			productsInConfiguration.forEach((p) => {
				p.sprItems?.forEach((childItem) => {
					articlesInConfiguration.push({
						id: childItem.localId || childItem.itemno,
						quantity: 1,
						type: 'ART',
					});
				});
			});

			const additionalCoverProducts: IShoppingProduct[] =
				await ProductUtils.getAdditionalCoverProducts(settings, articlesInConfiguration);

			const availableItems: ShoppingItem[] = [];

			await Promise.all([additionalCoverProducts]).then(async () => {
				additionalCoverProducts.forEach(async (p) => {
					availableItems.push({
						id: p.product.content?.itemNoLocal || p.product.content?.itemNoGlobal || '',
						quantity: p.quantity,
						type: 'ART',
					});
				});
			});

			return availableItems.length === coverItemsInConfiguration.length;
		}).then((results) => setAvailableCovers(results));
	}

	async function setCovers(): Promise<void> {
		if (coverOnly) {
			setAvailableCovers(coverOnlyItems);
		} else if (props.isAdditionalCover) {
			setAvailableAdditionalCovers();
		} else {
			setAvailableCovers(covers);
		}
	}

	async function getCoverItemsShoppingProducts(): Promise<void> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const coverItems: ShoppingItem[] = await getAdditionalCoverItems(activeCover);

		Promise.all([coverItems]).then(async () => {
			const coverItemsShoppingProducts: IShoppingProduct[] =
				await productService.getShoppingProducts(coverItems, KompisDefaultFields);
			setCoverItemsShoppingProduct(coverItemsShoppingProducts);
		});
	}

	if (missingCover) {
		return (
			<MissingCoverPicker
				onClick={(): void => {
					const configurationModelIds = ProductUtils.getModelIdsForConfiguration(
						products,
						missingProducts,
						missingConfiguration.configuration.children as SofaConfigurationItem[],
						rangeSettings.shouldColorAllModules as boolean,
						false
					);

					const availableCovers = ProductUtils.getAllAvailableCovers(
						products,
						covers,
						configurationModelIds
					);

					const newConfiguration = ProductUtils.getNewConfiguration(
						products,
						missingProducts,
						missingConfiguration.configuration.children as SofaConfigurationItem[],
						false,
						rangeSettings.shouldColorAllModules as boolean,
						selectedCover || availableCovers[0]
					);

					missingConfiguration.configuration.children = newConfiguration;
					dispatch(setSelectedConfiguration(missingConfiguration));
					dispatch(setMissingConfiguration(null));
					dispatch(setMissingCover(false));

					setVisible(false);
					setTimeout(() => {
						dispatch(setCurrentLightbox('none'));
					}, 100);
				}}
				onCoverClick={onCoverClick}
				onProductDescriptionExpand={(): void => {
					setProductDescriptionOpen(!productDescriptionOpen);
					dispatch(showCoverProductDescription(!productDescriptionOpen));
				}}
				platform={platform}
				deviceType={deviceType}
				orientation={orientation}
				images={activeCover.images as ImageItem[]}
				covers={coverOnly ? coverOnlyItems : covers}
				showSheet={visible}
				nrOfEmptyElements={getNrOfEmptyElements(
					orientation === 'portrait' ? 5 : 8,
					missingCover ? replaceCovers : coverOnly ? coverOnlyItems : covers
				)}
				replaceCovers={replaceCovers}
				missingCover={missingCover}
				sofaRangeName={rangeSettings.rangeName as SofaRangeName}
				selectedCover={selectedCover}
				selectedIndex={selectedIndex}
				onChange={(index): void => setSelectedIndex(index)}
				kompisSettings={kompisSettings}
				kompisTranslations={kompisTranslations}
				productDescriptionOpen={productDescriptionOpen}
			/>
		);
	}

	const sortImages = (images: ImageItem[]): ImageItem[] => {
		// Quality Product Picture should be shown as first image
		const index = images.findIndex((image) => image.typeName === 'Quality Product Picture');
		if (index > 0) {
			const firstItem = images[index];
			const allOthers = images.filter((image) => image !== firstItem);
			return [images[index], ...allOthers];
		} else {
			return images;
		}
	};

	return (
		<CoverPicker
			onSkipListing={onSkipListing}
			onClose={onClose}
			onCoverClick={onCoverClick}
			onChange={(index): void => {
				if (activeCover.images) {
					dispatch(
						setThumbnailImageChanged({
							imageUrl: activeCover.images[index].src,
							origin: 'cover-picker',
						})
					);
				}
				setSelectedIndex(index);
			}}
			onAddAdditionalCoverClick={onAddAdditionalCoverClick}
			platform={platform}
			deviceType={deviceType}
			orientation={orientation}
			writeDirection={writeDirection}
			images={activeCover.images ? sortImages(activeCover.images) : []}
			covers={availableCovers}
			showSheet={visible}
			nrOfEmptyElements={getNrOfEmptyElements(
				orientation === 'portrait' ? 5 : 8,
				missingCover ? replaceCovers : coverOnly ? coverOnlyItems : availableCovers
			)}
			replaceCovers={replaceCovers}
			missingCover={missingCover}
			sofaRangeName={rangeSettings.rangeName as SofaRangeName}
			selectedCover={activeCover}
			selectedIndex={selectedIndex}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			isAdditionalCover={props.isAdditionalCover}
			additionalCoverItemsProducts={additionalCoverItemsProducts}
			stockedIkeaPlus={stockedIkeaPlus}
		/>
	);
};

export default CoverPickerContainer;
